import { html, css, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { SplitContainerItem, ComponentFactory, ComponentStore, ComponentId, dom, ColorTypes } from "@omegagrid/core";
import { Tabs } from "@omegagrid/tabs";
import { msg } from "@omegagrid/localize";
import { ColumnSource, RowSource, CellModel } from "@omegagrid/grid";
import { FormContainer, FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormValues, FormControlProperties, FormEvent} from '@omegagrid/form';
import { TreeSource } from "@omegagrid/tree";
import rp, { AppComponent, GridEditor, GridEditorAdapter, GridEditorOptions, helpers } from "@reporting/rp-app-host";
import { DimAttributeValueTreeAdapter, DimTreeAdapter } from '../adapters';
import { DimensionDefinition, PeriodCloseDefinition } from "@reporting/rp-types/definition";
import { MODULE_CODES } from "@reporting/rp-types/accounting";

type ClosingPeriodItem = {
	PERIOD: string,
	YEAR: number,
	YYYYMM: number
};

/** A row of the closings grid - plain db columns, incl. the `DIM_<n>` / `PROPERTY_<n>` ones */
type ClosingRow = Record<string, string|number>;

/** Finance dimensions the period can be closed by */
function findClosingDimensions(): Promise<DimensionDefinition[]> {
	return rp.ms('definition')
		.get<DimensionDefinition[]>(`/visibleDimensions/${MODULE_CODES.FINANCE}`)
		.then(dims => dims.filter(dim => dim.period_close).map(dim => ({
			...dim,
			properties: dim.properties ? Object.values(dim.properties) : []
		})));
}

/** Turns the stored `{"<attribute key>": "<attribute value>"}` pair into a readable label */
function formatAttribute(dim: DimensionDefinition, value: string): string {
	if (!value) return '';
	try {
		return Object.entries(JSON.parse(value) as Record<string, string>)
			.map(([key, val]) => `${dim.properties?.find(prop => prop.key == key)?.name || key}: ${val}`)
			.join(', ');
	} catch {
		return value;
	}
}

class PeriodClosingGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _dims: Promise<DimensionDefinition[]>;

	private getDims(): Promise<DimensionDefinition[]> {
		return this._dims ??= findClosingDimensions();
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'openPeriod',
				icon: 'lock-open',
				title: msg('Open period'),
				color: ColorTypes.Red
			}],
			filter: false
		}, {
			data: 'PERIOD',
			header: msg('Period'),
			t: 't',
			w: 80
		}, {
			data: 'WHO_CLOSED_DV',
			header: msg('Closed by'),
			t: 't',
			w: 150
		}, {
			data: 'DATE_CLOSED',
			header: msg('Closing time'),
			t: 'dt',
			w: 145
		}, {
			data: 'A_B',
			header: msg('Scenario'),
			t: 't',
			w: 90
		}, {
			data: 'DATE_CUT',
			header: msg('Cut date'),
			t: 'dt',
			w: 145
		}];
		const dims = await this.getDims();
		dims.forEach(dim => {
			this._columns.push({
				data: `DIM_${dim.dim_num}`,
				header: dim.description,
				t: 't',
				w: 200
			});
			if (dim.properties?.length) {
				this._columns.push({
					data: `PROPERTY_${dim.dim_num}`,
					header: `${dim.description} - ${msg('Attributes')}`,
					t: 't',
					w: 200
				});
			}
		});
		this._columns.push({
			data: 'IDZ',
			t: 'n',
			hidden: true
		});
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const dims = await this.getDims();
		const closings = await rp.ms('definition').get<ClosingRow[]>('/periodClosings');
		return closings.map(data => {
			dims.forEach(dim => {
				const key = `PROPERTY_${dim.dim_num}`;
				if (data[key]) data[key] = formatAttribute(dim, data[key] as string);
			});
			return { data };
		});
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			refreshAfterSave: false,
			editable: false,
			toolbar: {
				save: {enabled: false},
				discard: {enabled: false},
				new: {enabled: false},
			},
			pageSize: null,
		});
	}

	onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'openPeriod':
				this.openPeriodClosing(cell.row.data.IDZ);
				break;
		}
	}

	async openPeriodClosing(idz: number) {
		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: msg('Are you sure you want to open the period?')
		});
		if (command?.id == 'Y') {
			await rp.ms('definition').delete(`periodClosings/${idz}`);
			this.editor.refresh();
		}
	}
}

class PeriodClosingFormAdapter extends FormAdapter {

	private formFields = [
		'yyyymm', 'a_b', 'date_cut',
		'id_dim_1', 'id_dim_2', 'id_dim_3', 'id_dim_4', 'id_dim_5', 'id_dim_6', 'id_dim_7',
		'property_1', 'property_2', 'property_3', 'property_4', 'property_5', 'property_6', 'property_7',
	] as const;

	/** @param compact mobile layout: a narrower label column leaves room for the lists on a phone-wide form */
	constructor(private compact = false) { super(); }

	getColumns() {
		return [
			{width: this.compact ? 120 : 180},
			{width: null},
		];
	}

	async getMonths(): Promise<TreeSource> {
		const items: TreeSource = [];
		const months = await rp.ms('analytics').get<ClosingPeriodItem[]>('months');
		let lastYear = 0;
		months.forEach(month => {
			if (month.YEAR !== lastYear) {
				items.push({
					k: month.YEAR.toString(),
					v: month.YEAR.toString(),
					l: 0,
					collapsed: lastYear ? true : false,
					ch: false,
				});
				lastYear = month.YEAR;
			}
			items.push({
				k: month.YYYYMM.toString(),
				v: month.PERIOD,
				l: 1,
				props: month
			});
		});
		return items;
	}

	async getScenarios(): Promise<TreeSource> {
		const items: TreeSource = [];
		const scenarios = await rp.ms('analytics').get<string[]>('/scenarios');
		scenarios.forEach(scenario => {
			items.push({
				k: scenario,
				v: scenario,
				l: 0
			});
		});
		return items;
	}

	async getControls() {
		const dims = await findClosingDimensions();
		const scenarios = await this.getScenarios();
		const periods = await this.getMonths();
		let rowIndex = 0;
		const rows = [
			{row: rowIndex, col: 0, type: FormControlType.Label, value: msg('Period'), for: 'yyyymm'},
			{row: rowIndex, col: 1, type: FormControlType.List, key: 'yyyymm', required: true, value: periods[1]?.k, content: {
				sourceData: periods, options: {selectMode: 'single', autoClose: true, allowNull: false}
			}},
			{row: ++rowIndex, col: 0, type: FormControlType.Label, value: msg('Scenario'), for: 'a_b'},
			{row: rowIndex, col: 1, type: FormControlType.List, key: 'a_b', required: true, value: scenarios[0]?.k, content: {
				sourceData: scenarios, options: {selectMode: 'single', autoClose: true}
			}},
			{row: ++rowIndex, col: 0, type: FormControlType.Label, value: msg('Cut date'), for: 'date_cut'},
			{row: rowIndex, col: 1, type: FormControlType.CalendarDateInput, key: 'date_cut', required: false, value: helpers.dateYearsFromNow(), options: {
				minDate: helpers.dateYearsFromNow(-10),
				maxDate: helpers.dateYearsFromNow(10),
			}}
		] as FormControlProperties[];
		if (dims.length) {
			rows.push({row: ++rowIndex, col: 0, type: FormControlType.Label});
			dims.forEach(dim => {
				rows.push(
					{row: ++rowIndex, col: 0, type: FormControlType.Label, value: dim.description, for: `id_dim_${dim.dim_num}`},
					{row: rowIndex, col: 1, type: FormControlType.List, key: `id_dim_${dim.dim_num}`, adapter: new DimTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num, {selectMode: 'single', autoClose: true})}
				);
				if (dim.properties?.length) {
					rows.push(
						{row: ++rowIndex, col: 0, type: FormControlType.Label, value: `${dim.description} - ${msg('Attributes')}`, for: `property_${dim.dim_num}`},
						{row: rowIndex, col: 1, type: FormControlType.List, key: `property_${dim.dim_num}`, adapter: new DimAttributeValueTreeAdapter(MODULE_CODES.FINANCE, dim.dim_num, dim.properties, {selectMode: 'single', autoClose: true})}
					);
				}
			});
		}
		rows.push({row: ++rowIndex, col: 0, type: FormControlType.Label});
		rows.push({row: ++rowIndex, col: 0, cs: 2, type: FormControlType.Button, key: 'closePeriodBtn', value: msg('Close period'), options: {action: 'save'}});
		return rows;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			alertsPosition: 'top',
			buttonsPosition: 'bottom',
			buttons: []
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: msg('Are you sure you want to close the period?')
		});
		if (command?.id == 'Y') {
			const closing: PeriodCloseDefinition = {
				...Object.fromEntries(this.formFields.map(key => [key, formValues[key]]))
			};
			await rp.ms('definition').post('periodClosings', closing);
			return true;
		}
		return false;
	}
};

@customElement('rp-definition-finperiodclosing')
export class FinPeriodClosingContainer extends AppComponent {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
			display: block;
		}

		og-button {
			flex: 1;
		}

		.compact {
			display: flex;
			flex-direction: column;
			height: 100%;
		}
		.compact-controls {
			flex: 0 1 auto;
			min-height: 0;
			max-height: 55%;
			overflow: auto;
			border-bottom: 1px solid var(--og-border-color);
		}
		.compact-content {
			flex: 1 1 auto;
			min-height: 0;
		}
	`;

	@property({type: Array})
	items: SplitContainerItem[] = [{id: 'constrols', size: 400}, {id: 'grid', size: null}];

	/** Set by the host on open and resize. Mobile layout: the form above the grid without a splitter, decided once at first render. */
	@property({type: Boolean})
	compact = !!rp.host?.compact;

	@property({type: Object})
	componentFactory: ComponentFactory;

	@state()
	private _ready = false;

	@state()
	private _compactLayout = false;

	@query('og-splitcontainer')
	splitContainer: Tabs;

	private form: FormContainer;
	private grid: GridEditor;
	private _resizeObserver: ResizeObserver;

	componentStore = new ComponentStore(async (id: ComponentId) => {
		switch (id) {
			case 'constrols':
				this.form = dom.createElement<FormContainer>('og-form-container');
				this.form.adapter = new PeriodClosingFormAdapter(this.compact);
				this.form.addEventListener('form.change', (e: FormEvent) => {
					if (e.key === 'closePeriodBtn') this.form.save();
				});
				this.form.addEventListener('form.save', () => this.refreshComponent('grid'));
				return this.form;
			case 'grid':
				this.grid = dom.createElement<GridEditor>('rp-editor-grid');
				this.grid.adapter = new PeriodClosingGridAdapter();
				return this.grid;
		}
	});

	async firstUpdated() {
		this._compactLayout = this.compact;
		if (this._compactLayout) {
			await this.componentStore.get('constrols');
			await this.componentStore.get('grid');
			this._observeGrid();
		}
		this._ready = true;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this._compactLayout) this._observeGrid();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._resizeObserver?.disconnect();
		this._resizeObserver = null;
	}

	/** In the mobile layout the form above grows as its lists load, so the grid must be laid out again on every size change (the split container does this on the desktop). */
	private _observeGrid() {
		if (!this.grid || this._resizeObserver) return;
		this._resizeObserver = new ResizeObserver(() => this.grid.layout());
		this._resizeObserver.observe(this.grid);
	}

	refreshComponent(id: ComponentId) {
		this.componentStore.get(id).then(component => {
			if (typeof (component as any)?.refresh === 'function') {
				(component as any).refresh();
			}
		});
	}

	layout() {
		if (!this._compactLayout) return; // the split container lays out its own children
		this.grid?.layout();
	}

	render = () => {
		if (!this._ready) return nothing;
		if (this._compactLayout) {
			return html`
				<div class="compact">
					<div class="compact-controls">${this.form}</div>
					<div class="compact-content">${this.grid}</div>
				</div>
			`;
		}
		return html`
			<og-splitcontainer
				orientation="horizontal"
				.createComponent="${(id: ComponentId) => this.componentStore.get(id)}"
				.items="${this.items}"
				style="height: 100%;">
			</og-splitcontainer>
		`;
	};
}
